import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import backgroundImage from "../../assets/img/fondo_camino_niveles.svg";
import nivel1 from "../../assets/img/niveles/Nivel1.svg";
import nivel1Completo from "../../assets/img/niveles/Nivel1Completo.svg";
import nivel2 from "../../assets/img/niveles/Nivel2.svg";
import nivel2Completo from "../../assets/img/niveles/Nivel2Completo.svg";
import nivel3 from "../../assets/img/niveles/Nivel3.svg";
import nivel3Completo from "../../assets/img/niveles/Nivel3Completo.svg";
import nivel4 from "../../assets/img/niveles/Nivel4.svg";
import nivel4Completo from "../../assets/img/niveles/Nivel4Completo.svg";
import nivel5 from "../../assets/img/niveles/Nivel5.svg";
import nivel5Completo from "../../assets/img/niveles/Nivel5Completo.svg";
import banderaResultados from "../../assets/img/niveles/bandera1.svg";
import banderaResultadosActiva from "../../assets/img/niveles/bandera2.svg";
import banderaTheoResutados from "../../assets/img/niveles/banderaTheoResutados.gif";
import caminoNiveles from "../../assets/img/niveles/caminoNiveles3.svg";
import logoCIE from "../../assets/img/logoCIE.svg";
import useNivelesUsuario from "../../hooks/useNivelesUsuario";
import HeaderUsuario from "../header/HeaderUsuario";
import TablaProgreso from "./TablaProgreso";

// GIFs del personaje
import caminanteWalkGif from "../../assets/img/niveles/caminante.gif"; // caminando
import caminanteIdleGif from "../../assets/img/niveles/caminante_quieto.gif"; // quieto (nuevo)

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const BUBBLE_OFFSET_X = -100; // + derecha, - izquierda (px)
const BUBBLE_OFFSET_Y = -20; // + abajo, - arriba (px)

// Tamaño base del escenario (tu diseño original)
const BASE_W = 1100;
const BASE_H = 530;

const NivelesUsuario = () => {
  const {
    niveles,
    mensaje,
    mostrarMensaje,
    nivelPositions,
    banderaResultadosPosition,
    navigate,
    cerrarVentana,
    mostrarVentana,
    todosNivelesCompletados,
    usuario,
  } = useNivelesUsuario();

  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [modalNivelSeleccionado, setModalNivelSeleccionado] = useState(null);

  // Estado para validar “Duelo de saberes” por nivel
  const [dueloLoading, setDueloLoading] = useState(false);
  const [dueloCompleto, setDueloCompleto] = useState(null); // true/false/null
  const [dueloCache, setDueloCache] = useState({}); // { [nivelId]: boolean }

  const puntajeTotal = niveles.reduce(
    (total, nivel) => total + (nivel.respuestas_correctas || 0),
    0
  );

  const nivelesFiltrados = niveles;

  // ====== WALKER: animación siguiendo la carretera ======
  const pathOffsets = useMemo(
    () => [
      { dx: 8, dy: 200 }, // PIN 01
      { dx: 6, dy: 180 }, // PIN 02
      { dx: -10, dy: 160 }, // PIN 03
      { dx: 10, dy: 200 }, // PIN 04
      { dx: -15, dy: 150 }, // PIN 05 (si aplica)
    ],
    []
  );

  const pathPos = useMemo(() => {
    const base = [
      nivelPositions[0],
      nivelPositions[1],
      nivelPositions[2],
      nivelPositions[3],
      nivelPositions[4],
    ];
    return base
      .map((p, i) =>
        p
          ? {
              x: p.x + (pathOffsets[i]?.dx || 0),
              y: p.y + (pathOffsets[i]?.dy || 0),
            }
          : null
      )
      .filter(Boolean);
  }, [nivelPositions, pathOffsets]);

  const [walker, setWalker] = useState(() => ({
    x: pathPos[0]?.x ?? 0,
    y: pathPos[0]?.y ?? 0,
    idx: 0,
    moving: false,
    flip: false,
  }));

  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const qBezier = (p0, p1, p2, t) => {
    const u = 1 - t;
    return {
      x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
      y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
    };
  };

  const controlPoint = (p0, p2) => ({
    x: (p0.x + p2.x) / 2,
    y: Math.min(p0.y, p2.y) - 5,
  });

  const WALK_DURATION = 5000;

  const animateWalker = (fromIdx, toIdx, duration = WALK_DURATION) => {
    if (!pathPos[fromIdx] || !pathPos[toIdx]) return;
    const p0 = pathPos[fromIdx];
    const p2 = pathPos[toIdx];
    const p1 = controlPoint(p0, p2);
    const flip = p2.x < p0.x;
    setWalker((w) => ({ ...w, moving: true, flip }));

    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      const tt = easeInOutCubic(t);
      const { x, y } = qBezier(p0, p1, p2, tt);
      setWalker((w) => ({ ...w, x, y }));
      if (t < 1) requestAnimationFrame(step);
      else {
        setWalker((w) => ({
          ...w,
          idx: toIdx,
          moving: false,
          x: p2.x,
          y: p2.y,
        }));
      }
    };
    requestAnimationFrame(step);
  };

  useEffect(() => {
    if (!niveles?.length || !pathPos.length) return;
    let lastCompleted = -1;
    for (let i = 0; i < niveles.length; i++) {
      if (niveles[i].completado) lastCompleted = i;
    }
    const nextActive = niveles.findIndex(
      (n) => n.desbloqueado && !n.completado
    );

    if (lastCompleted < 0) {
      if (
        walker.idx !== 0 ||
        walker.x !== pathPos[0].x ||
        walker.y !== pathPos[0].y
      ) {
        setWalker((w) => ({
          ...w,
          idx: 0,
          x: pathPos[0].x,
          y: pathPos[0].y,
          moving: false,
          flip: false,
        }));
      }
      return;
    }

    if (
      nextActive >= 0 &&
      lastCompleted >= 0 &&
      nextActive === lastCompleted + 1 &&
      walker.idx !== nextActive &&
      !walker.moving
    ) {
      animateWalker(lastCompleted, nextActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [niveles, pathPos.length]);
  // ====== /WALKER ======

  // ---- Validación duelo por nivel ----
  const validarDueloPorNivel = async (nivelId) => {
    if (!usuario?.id || !nivelId) return;
    if (dueloCache[nivelId] !== undefined) {
      setDueloCompleto(dueloCache[nivelId]);
      return;
    }
    setDueloLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${API_BASE_URL}/api/temas/info/${nivelId}/${usuario.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { t: Date.now() },
        }
      );
      const ok =
        Array.isArray(data) &&
        data.length > 0 &&
        data.every(
          (d) =>
            d.completado === 1 || d.completado === "1" || d.completado === true
        );
      setDueloCompleto(ok);
      setDueloCache((prev) => ({ ...prev, [nivelId]: ok }));
    } catch (err) {
      console.error("❌ Error al validar Duelo de saberes:", err);
      setDueloCompleto(false);
    } finally {
      setDueloLoading(false);
    }
  };

  useEffect(() => {
    if (modalNivelSeleccionado?.id) {
      validarDueloPorNivel(modalNivelSeleccionado.id);
    } else {
      setDueloCompleto(null);
      setDueloLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalNivelSeleccionado?.id]);

  // ====== RESPONSIVE STAGE (escalado) ======
  const stageWrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const recompute = () => {
      const w = stageWrapRef.current?.clientWidth || window.innerWidth;
      const s = Math.min(w / BASE_W, 1); // no ampliar por encima del 100%
      setScale(s);
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, []);
  // =========================================

  return (
    <div
      className="min-h-[100dvh] flex flex-col font-montserrat relative"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* VISTA PUNTAJE GENERAL */}
      <div className="absolute top-2 left-2 sm:top-6 sm:left-6 z-20 bg-green-700 text-white px-4 py-3 sm:px-6 sm:py-4 rounded-lg shadow-lg text-sm sm:text-lg font-bold flex flex-col gap-2">
        <div>Progreso general: {puntajeTotal} puntos</div>
        <button
          className="bg-white text-green-700 font-semibold px-3 py-2 sm:px-4 sm:py-2 rounded hover:bg-green-100 transition"
          onClick={() => setMostrarTabla(!mostrarTabla)}
        >
          Resultados generales
        </button>
        {mostrarTabla && (
          <TablaProgreso
            onClose={() => setMostrarTabla(false)}
            niveles={niveles}
            usuario={usuario}
          />
        )}
      </div>

      {/* Header */}
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10">
        <HeaderUsuario />
      </div>

      {/* ESCENARIO (carretera + pines + caminante) */}
      <main className="flex-grow flex items-center justify-center p-4">
        <div ref={stageWrapRef} className="w-full max-w-[1160px] mx-auto">
          {/* Reservar espacio del tamaño escalado (evita overflow) */}
          <div
            style={{
              width: BASE_W * scale,
              height: BASE_H * scale,
              position: "relative",
              margin: "0 auto",
            }}
          >
            {/* Escenario base (1100x530) escalado */}
            <div
              style={{
                width: BASE_W,
                height: BASE_H,
                position: "relative",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            >
              <img
                src={caminoNiveles}
                alt="Camino de Niveles"
                className="w-full h-full mt-10 object-contain"
                style={{ position: "relative", zIndex: 0 }}
              />

              {/* Caminante */}
              {!todosNivelesCompletados() && (
                <img
                  src={walker.moving ? caminanteWalkGif : caminanteIdleGif}
                  alt={walker.moving ? "Caminando" : "Quieto"}
                  className="absolute select-none pointer-events-none z-30"
                  style={{
                    left: `${walker.x}px`,
                    top: `${walker.y}px`,
                    transform: `translate(-50%, -100%) scaleX(${
                      walker.flip ? -1 : 1
                    })`,
                    width: "72px",
                    height: "auto",
                    filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.25))",
                    willChange: "transform",
                  }}
                />
              )}

              {/* Pines de niveles */}
              {nivelesFiltrados.map((nivel, idx) => {
                const imagenes = [
                  { base: nivel1, completo: nivel1Completo },
                  { base: nivel2, completo: nivel2Completo },
                  { base: nivel3, completo: nivel3Completo },
                  { base: nivel4, completo: nivel4Completo },
                  { base: nivel5, completo: nivel5Completo },
                ];

                const idxVisual = Math.min(idx, imagenes.length - 1);
                const sprite = imagenes[idxVisual];
                const posPin = nivelPositions[idxVisual];
                if (!sprite || !posPin) return null;

                const imagen = nivel.completado ? sprite.completo : sprite.base;
                const label = String(idxVisual + 1).padStart(2, "0");

                return (
                  <div
                    key={nivel.id}
                    style={{
                      position: "absolute",
                      left: `${posPin.x}px`,
                      top: `${posPin.y}px`,
                      zIndex: 20,
                      pointerEvents: "auto",
                    }}
                  >
                    <div className="relative">
                      <button
                        onClick={() => setModalNivelSeleccionado(nivel)}
                        className="cursor-pointer relative"
                        style={{ zIndex: 21 }}
                      >
                        <img
                          src={imagen}
                          alt={`Nivel ${label}`}
                          className={nivel.esActivo ? "animate-bounce" : ""}
                          style={{ display: "block", pointerEvents: "none" }}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Bandera de resultados */}
              <div
                className={`absolute z-10 ${
                  todosNivelesCompletados()
                    ? "cursor-pointer"
                    : "cursor-not-allowed"
                }`}
                style={{
                  left: `${banderaResultadosPosition.x}px`,
                  top: `${banderaResultadosPosition.y}px`,
                }}
                onClick={() => {
                  if (todosNivelesCompletados()) navigate("/trofeo-usuario");
                  else
                    mostrarVentana(
                      "Completa todos los niveles para ver los resultados."
                    );
                }}
              >
                {todosNivelesCompletados() ? (
                  <div className="relative w-fit">
                    {/* 🗨️ Burbuja */}
                    <div
                      className="absolute bottom-full left-1/2 pointer-events-none"
                      style={{
                        transform: `translate(calc(-50% + ${BUBBLE_OFFSET_X}px), ${BUBBLE_OFFSET_Y}px)`,
                      }}
                    >
                      <div
                        className="bg-white text-green-800 font-medium text-sm leading-snug 
                        px-4 py-3 rounded-2xl shadow-lg border-2 border-green-600 
                        text-left w-[160px] break-words"
                      >
                        ¡Descarga tu constancia aquí!
                      </div>
                      {/* Colita */}
                      <div
                        className="absolute -bottom-2 right-3
                        w-3.5 h-3.5 bg-white rotate-45
                        border-l-2 border-t-2 border-green-600"
                      />
                    </div>

                    {/* Bandera final (transform responsivo) */}
                    <img
                      src={banderaTheoResutados}
                      alt="Resultados"
                      className="h-auto w-32 scale-[1.6] sm:w-40 transform origin-bottom-right sm:scale-[1.5] sm:translate-x-6 sm:translate-y-5"
                    />
                  </div>
                ) : (
                  <img
                    src={banderaResultados}
                    alt="Resultados"
                    className="h-auto w-16 sm:w-40 opacity-50"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal mensaje */}
      {mostrarMensaje && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={cerrarVentana}
              className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 focus:outline-none"
            >
              ✕
            </button>
            <p
              className={`text-center ${
                mensaje.includes("Error") || mensaje.includes("SIN PREGUNTAS")
                  ? "text-red-600"
                  : "text-green-600"
              } text-sm sm:text-base font-bold`}
            >
              {mensaje}
            </p>
          </div>
        </div>
      )}

      {/* Modal selección nivel */}
      {modalNivelSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80 relative shadow-xl">
            <button
              onClick={() => setModalNivelSeleccionado(null)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            {(() => {
              const idxVisual = nivelesFiltrados.findIndex(
                (n) => n.id === modalNivelSeleccionado.id
              );
              const labelVisual =
                idxVisual >= 0
                  ? String(idxVisual + 1).padStart(2, "0")
                  : String(modalNivelSeleccionado.orden).padStart(2, "0");

              const puedeDuelo =
                !!modalNivelSeleccionado?.esActivo &&
                !modalNivelSeleccionado?.completado;

              const puedeFinal =
                modalNivelSeleccionado?.esActivo &&
                dueloCompleto === true &&
                !dueloLoading;

              const puedeRevisar = dueloCompleto === true && !dueloLoading;

              return (
                <>
                  <h2 className="text-2xl font-extrabold text-black text-center mb-4">
                    NIVEL {labelVisual}
                  </h2>

                  <div className="flex flex-col gap-4">
                    <button
                      disabled={!puedeDuelo}
                      onClick={() => {
                        if (!puedeDuelo) {
                          mostrarVentana(
                            modalNivelSeleccionado?.completado
                              ? "Nivel completado. Usa 'Contenido' para revisar el material."
                              : "Debes completar el nivel anterior para iniciar el Duelo de saberes."
                          );
                          return;
                        }
                        setModalNivelSeleccionado(null);
                        navigate(`/quizzes/tema/${modalNivelSeleccionado.id}`);
                      }}
                      className={`${
                        !puedeDuelo
                          ? "bg-gray-500 text-white cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
                      } justify-between rounded-lg px-8 py-4 transition-transform ${
                        !puedeDuelo ? "" : "transform hover:scale-105"
                      } w-full hover:shadow-[0_9px_20px_rgba(0,0,0,0.4)]`}
                    >
                      ¡Duelo de saberes!
                    </button>

                    <div className="flex flex-col gap-2">
                      <button
                        disabled={!puedeFinal}
                        onClick={() => {
                          if (puedeFinal) {
                            setModalNivelSeleccionado(null);
                            navigate(
                              `/evaluacion-final/nivel/${modalNivelSeleccionado.id}`
                            );
                          } else {
                            if (dueloLoading) {
                              mostrarVentana(
                                "Validando requisitos del nivel, por favor intenta nuevamente en un instante."
                              );
                            } else {
                              mostrarVentana(
                                "Completa primero el Duelo de saberes (todos los temas) para habilitar el Desafío final."
                              );
                            }
                          }
                        }}
                        className={`${
                          !puedeFinal
                            ? "bg-gray-500 text-white cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
                        } rounded-lg px-8 py-4 transition-transform ${
                          !puedeFinal ? "" : "transform hover:scale-105"
                        } w-full hover:shadow-[0_9px_20px_rgba(0,0,0,0.4)]`}
                      >
                        {dueloLoading
                          ? "Validando requisitos..."
                          : "Desafío final"}
                      </button>

                      {dueloCompleto === false && !dueloLoading && (
                        <p className="text-xs text-gray-600 text-center">
                          🔒 Requisito: completar todos los temas del Duelo de
                          saberes.
                        </p>
                      )}
                    </div>

                    <button
                      disabled={!puedeRevisar}
                      onClick={() => {
                        setModalNivelSeleccionado(null);
                        navigate(
                          `/temas/nivel/${modalNivelSeleccionado.id}?revision=1`
                        );
                      }}
                      title={
                        puedeRevisar
                          ? "Ver contenidos del nivel (modo revisión)"
                          : dueloLoading
                          ? "Validando requisitos..."
                          : "Disponible cuando completes todos los temas del nivel"
                      }
                      className={`${
                        !puedeRevisar
                          ? "bg-gray-500 text-white cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
                      } rounded-lg px-8 py-4 transition-transform ${
                        !puedeRevisar ? "" : "transform hover:scale-105"
                      } w-full hover:shadow-[0_9px_20px_rgba(0,0,0,0.4)]`}
                    >
                      Contenido
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Logo CIE */}
      <div className="absolute bottom-3 right-2 sm:bottom-4 sm:right-4 z-10">
        <img src={logoCIE} alt="Logo CIE" className="h-8 sm:h-10 w-auto" />
      </div>
    </div>
  );
};

export default NivelesUsuario;
